<template>
    <div>
        <HeaderPage href="/news" />
        <div class="container_wrap">
            <div class="neiBanner">
                <div class="textBanner wow">
                    <dl>NEWS CENTER</dl>
                    <dt>新闻中心</dt>
                </div>
            </div>
            <div class="news">
                <div class="wrap">
                    <div class="box wow">
                        <!-- 加载状态 -->
                        <div class="loading" v-if="loading">
                            <p>数据加载中...</p>
                        </div>
                        <!-- 数据列表 -->
                        <ul v-else-if="currentPageData.length">
                            <li v-for="(item) in currentPageData" :key="item.id">
                                <router-link :to="`/news/details?typeId=${id}&title=${item.title}`">
                                    <h3>{{ item.title }}</h3>
                                    <dd>{{ item.date }}</dd>
                                    <dt v-if="item.content" v-html="item.content.substring(0, 200) + '...'"></dt>
                                </router-link>
                            </li>
                        </ul>
                        <!-- 无数据提示 -->
                        <div class="no-data" v-else>
                            <p>暂无新闻数据</p>
                        </div>
                    </div>

                    <!-- 分页组件 -->
                    <div class="pagination" v-if="totalPage > 1">
                        <!-- 分页信息 -->
                        <div class="page-info">
                            共 {{ totalCount }} 条数据，当前第 {{ currentPage }} / {{ totalPage }} 页
                        </div>
                        <!-- 页码按钮 -->
                        <div class="page-buttons">
                            <!-- 上一页 -->
                            <button 
                                class="page-btn prev" 
                                @click="currentPage--" 
                                :disabled="currentPage === 1"
                            >
                                上一页
                            </button>
                            
                            <!-- 页码列表 -->
                            <button 
                                v-for="page in pageRange" 
                                :key="page" 
                                class="page-btn number"
                                :class="{ active: page === currentPage }"
                                @click="currentPage = page"
                            >
                                {{ page }}
                            </button>
                            
                            <!-- 下一页 -->
                            <button 
                                class="page-btn next" 
                                @click="currentPage++" 
                                :disabled="currentPage === totalPage"
                            >
                                下一页
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <FooterPage />
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed, watch } from 'vue';
import { useRoute } from 'vue-router';
import { getWebsiteData } from '@/utils/api';
// 组件逻辑
import HeaderPage from '@/components/HeaderPage.vue'
import FooterPage from '@/components/FooterPage.vue'

const route = useRoute();

// 从路由获取id，如果没有则使用默认值（比如新闻类型的id）
const id = ref(Number(route.query.id) || 1); // 默认获取type为1的新闻

// 监听路由变化，更新id
watch(() => route.query.id, (newId) => {
  if (newId) {
    id.value = Number(newId);
    currentPage.value = 1; // 重置页码
    fetchData(); // 重新获取数据
  }
});

// 1. 数据状态：原始总数据、分页相关状态
const pDataList = ref([]); // 接口返回的数据列表
const currentPage = ref(1); // 当前页码（默认第1页）
const pageSize = ref(12); // 每页显示10条（与接口保持一致）
const totalCount = ref(0); // 总数据量（从接口获取）
const loading = ref(false); // 加载状态

// 2. 分页核心计算属性
// 总页数：向上取整
const totalPage = computed(() => Math.ceil(totalCount.value / pageSize.value));

// 处理显示数据：将接口数据转换为页面需要的格式
const currentPageData = computed(() => {
    return pDataList.value.map(item => {
        // 处理时间格式
        let formattedDate = '';
        
        if (item.createTime || item.updateTime) {
            const timeStr = item.createTime || item.updateTime;
            const date = new Date(timeStr);
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const hours = String(date.getHours()).padStart(2, '0');
            const minutes = String(date.getMinutes()).padStart(2, '0');
            const seconds = String(date.getSeconds()).padStart(2, '0');
            formattedDate = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
        }
        
        return {
            id: item.id,
            title: item.title,
            subtitle: item.subtitle || '',
            content: item.content || '',
            type: item.type,
            date: formattedDate
        };
    });
});

// 3. 页码范围计算：最多显示5个页码
const pageRange = computed(() => {
    const range = [];
    if (totalPage.value <= 5) {
        for (let i = 1; i <= totalPage.value; i++) range.push(i);
    } else {
        let start = Math.max(1, currentPage.value - 2);
        let end = Math.min(totalPage.value, currentPage.value + 2);
        if (start === 1) end = 5;
        if (end === totalPage.value) start = totalPage.value - 4;
        for (let i = start; i <= end; i++) range.push(i);
    }
    return range;
});

// 获取数据的方法
const fetchData = async () => {
    try {
        loading.value = true;
        const params = {
            pageNo: currentPage.value,
            pageSize: pageSize.value,
            type: id.value
        };
        
        const response = await getWebsiteData(params);
        
        // 根据接口返回格式处理数据
        if (response && response.data) {
            pDataList.value = response.data.list || [];
            totalCount.value = response.data.total || 0;
        }
    } catch (error) {
        console.error('获取新闻数据失败：', error);
        pDataList.value = [];
        totalCount.value = 0;
    } finally {
        loading.value = false;
    }
};

// 监听页码变化，重新获取数据
watch(currentPage, () => {
    fetchData();
});

let intervalId

const stopAutoScroll = () => {
    clearInterval(intervalId)
}

onMounted(async () => {
    // 初始化获取数据
    await fetchData();

    // ✅ 滚动动画逻辑
    const elements = document.querySelectorAll('.wow')
    const observer = new IntersectionObserver(
        (entries) => {
            entries.forEach((entry) => {
                const el = entry.target
                const animateName = el.dataset.animate || 'fadeInUp'
                if (entry.isIntersecting) {
                    el.classList.add('animated', animateName)
                    // ✅ 添加后立即取消观察，避免重复触发
                    observer.unobserve(el)
                }
            })
        },
        { threshold: 0.2 }
    )
    elements.forEach((el) => observer.observe(el))
    onUnmounted(() => {
        elements.forEach((el) => observer.unobserve(el))
        stopAutoScroll()
    })
})
</script>

<style scoped>
.neiBanner {
    height: 320px;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    background: url('@/assets/images/bg/informationBg.jpg') no-repeat center;
}

.neiBanner .textBanner {
    color: #fff;
    text-align: center;
}

.neiBanner .textBanner dl {
    font-size: 42px;
    color: #003366;
}

.neiBanner .textBanner dt {
    font-size: 30px;
    color: #000;
}

.news {
    padding: 50px 0;
}

.news .wrap {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 20px;
}

.news .box ul {
    list-style: none;
    padding: 0;
    margin: 0;
}

.news .box li {
    margin-bottom: 30px;
    /* padding: 20px; */
    /* border: 1px solid #eee; */
    /* border-radius: 8px; */
    transition: all 0.3s ease;
}

.news .box li:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
}

.news .box li a {
    text-decoration: none;
    color: inherit;
    display: block;
}

.news .box li h3 {
    font-size: 18px;
    color: #333;
    margin-bottom: 10px;
    line-height: 1.4;
}

/* .news .box li dd {
    color: #666;
    font-size: 14px;
    margin-bottom: 15px;
} */

.news .box li dt {
    color: #666;
    font-size: 14px;
    line-height: 1.6;
}

/* 分页样式 */
.pagination {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 20px;
    margin: 30px 0;
    padding: 10px;
}

.page-info {
    color: #666;
    font-size: 14px;
}

.page-buttons {
    display: flex;
    gap: 8px;
}

.page-btn {
    padding: 5px 12px;
    border: 1px solid #ddd;
    background-color: #fff;
    color: #333;
    border-radius: 4px;
    cursor: pointer;
    font-size: 14px;
    transition: all 0.2s;
}

.page-btn:disabled {
    cursor: not-allowed;
    color: #999;
    background-color: #f5f5f5;
    border-color: #eee;
}

.page-btn.number.active {
    background-color: #003366;
    color: #fff;
    border-color: #003366;
}

.page-btn:hover:not(:disabled) {
    border-color: #003366;
    color: #003366;
}

/* 加载状态样式 */
.loading {
    text-align: center;
    padding: 50px 0;
    color: #666;
    font-size: 16px;
}

.no-data {
    text-align: center;
    padding: 50px 0;
    color: #999;
    font-size: 16px;
}
</style>